import { useEffect, useRef, useCallback, useState } from "react";
import L from "leaflet";
import { addBasemapLayer } from "@/lib/map-tiles";
import { MapPin, Crosshair } from "lucide-react";

interface LocationPickerMapProps {
    centerLat: number | null;
    centerLng: number | null;
    pinLat: number | null;
    pinLng: number | null;
    onPinChange: (lat: number, lng: number) => void;
    autoLocateOnMount?: boolean;
    /** Show a pulsing blue dot for the tech's real-time GPS position */
    showUserLocation?: boolean;
}

export const LEAFLET_CSS_ID = "leaflet-css-link";
export const LEAFLET_CSS_URL = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
export const DEFAULT_ZOOM = 18;
const FALLBACK_LAT = 33.07;
const FALLBACK_LNG = -97.56;

export function ensureLeafletCSS() {
    if (document.getElementById(LEAFLET_CSS_ID)) return;
    const link = document.createElement("link");
    link.id = LEAFLET_CSS_ID;
    link.rel = "stylesheet";
    link.href = LEAFLET_CSS_URL;
    document.head.appendChild(link);
}

export function LocationPickerMap({
    centerLat,
    centerLng,
    pinLat,
    pinLng,
    onPinChange,
    autoLocateOnMount = false,
    showUserLocation = false,
}: LocationPickerMapProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<L.Map | null>(null);
    const markerRef = useRef<L.Marker | null>(null);
    const blueDotRef = useRef<L.CircleMarker | null>(null);
    const blueDotRingRef = useRef<L.CircleMarker | null>(null);
    const onPinChangeRef = useRef(onPinChange);
    onPinChangeRef.current = onPinChange;
    const [locating, setLocating] = useState(false);

    const placePin = useCallback((lat: number, lng: number) => {
        const map = mapRef.current;
        if (!map) return;
        if (markerRef.current) {
            markerRef.current.setLatLng([lat, lng]);
        } else {
            const pin = L.marker([lat, lng], {
                draggable: true,
                icon: L.divIcon({
                    className: "location-picker-pin",
                    html: `<div style="width:28px;height:28px;border-radius:50%;background:#ea580c;border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.4);display:flex;align-items:center;justify-content:center;">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                    </div>`,
                    iconSize: [28, 28],
                    iconAnchor: [14, 14],
                }),
            }).addTo(map);
            pin.on("dragend", () => {
                const pos = pin.getLatLng();
                onPinChangeRef.current(pos.lat, pos.lng);
            });
            markerRef.current = pin;
        }
    }, []);

    const locateMe = useCallback(() => {
        if (!navigator.geolocation) return;
        setLocating(true);
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const lat = pos.coords.latitude;
                const lng = pos.coords.longitude;
                mapRef.current?.setView([lat, lng], DEFAULT_ZOOM);
                placePin(lat, lng);
                onPinChangeRef.current(lat, lng);
                setLocating(false);
            },
            () => setLocating(false),
            { enableHighAccuracy: true, timeout: 10000 },
        );
    }, [placePin]);

    useEffect(() => {
        ensureLeafletCSS();

        if (!containerRef.current || mapRef.current) return;

        const lat = centerLat ?? FALLBACK_LAT;
        const lng = centerLng ?? FALLBACK_LNG;

        const map = L.map(containerRef.current, {
            center: [lat, lng],
            zoom: DEFAULT_ZOOM,
            zoomControl: false,
            attributionControl: false,
        });

        addBasemapLayer(L, map, "satellite", { maxZoom: 20 });
        L.control.zoom({ position: "topright" }).addTo(map);
        mapRef.current = map;

        map.on("click", (e: L.LeafletMouseEvent) => {
            placePin(e.latlng.lat, e.latlng.lng);
            onPinChangeRef.current(e.latlng.lat, e.latlng.lng);
        });

        if (pinLat != null && pinLng != null) {
            placePin(pinLat, pinLng);
        } else if (autoLocateOnMount && navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    const gLat = pos.coords.latitude;
                    const gLng = pos.coords.longitude;
                    map.setView([gLat, gLng], DEFAULT_ZOOM);
                    placePin(gLat, gLng);
                    onPinChangeRef.current(gLat, gLng);
                },
                () => {},
                { enableHighAccuracy: true, timeout: 8000 },
            );
        }

        setTimeout(() => map.invalidateSize(), 200);

        return () => {
            map.remove();
            mapRef.current = null;
            markerRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Real-time blue dot for tech's GPS position
    useEffect(() => {
        if (!showUserLocation || !navigator.geolocation) return;
        const watchId = navigator.geolocation.watchPosition(
            (pos) => {
                const map = mapRef.current;
                if (!map) return;
                const lat = pos.coords.latitude;
                const lng = pos.coords.longitude;
                if (blueDotRef.current) {
                    blueDotRef.current.setLatLng([lat, lng]);
                    blueDotRingRef.current?.setLatLng([lat, lng]);
                } else {
                    blueDotRingRef.current = L.circleMarker([lat, lng], {
                        radius: 18,
                        color: "rgba(66,133,244,0.25)",
                        fillColor: "rgba(66,133,244,0.12)",
                        fillOpacity: 1,
                        weight: 0,
                        className: "blue-dot-ring",
                    }).addTo(map);
                    blueDotRef.current = L.circleMarker([lat, lng], {
                        radius: 7,
                        color: "#fff",
                        fillColor: "#4285F4",
                        fillOpacity: 1,
                        weight: 2.5,
                    }).addTo(map);
                }
            },
            () => {},
            { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 },
        );
        return () => {
            navigator.geolocation.clearWatch(watchId);
            if (blueDotRef.current && mapRef.current) {
                mapRef.current.removeLayer(blueDotRef.current);
                blueDotRef.current = null;
            }
            if (blueDotRingRef.current && mapRef.current) {
                mapRef.current.removeLayer(blueDotRingRef.current);
                blueDotRingRef.current = null;
            }
        };
    }, [showUserLocation]);

    useEffect(() => {
        if (!mapRef.current) return;
        if (pinLat != null && pinLng != null) {
            placePin(pinLat, pinLng);
        }
    }, [pinLat, pinLng, placePin]);

    return (
        <div className="rounded-lg overflow-hidden border border-border">
            <div className="relative">
                <div ref={containerRef} style={{ height: 220, width: "100%" }} />
                <button
                    type="button"
                    onClick={locateMe}
                    disabled={locating}
                    className="absolute bottom-3 left-3 z-[1000] flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/90 shadow-md border text-xs font-medium text-gray-700 hover:bg-white transition-colors disabled:opacity-50"
                >
                    <Crosshair className={`h-3.5 w-3.5 ${locating ? "animate-spin" : ""}`} />
                    {locating ? "Locating…" : "Use my location"}
                </button>
            </div>
            <div className="flex items-center gap-1.5 px-2 py-1.5 bg-muted/50 text-xs text-foreground/80">
                <MapPin className="h-3 w-3 shrink-0" />
                Tap map or drag pin to set device location
            </div>
        </div>
    );
}
